import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  getMaxTransitionDurationInFrames,
  MIN_TRANSITION_DURATION_IN_FRAMES,
  TransitionItem,
} from '../items/transition/transition-item-type';
import {
  useAllItems,
  useAssets,
  useFps,
  useWriteContext,
} from '../utils/use-context';
import { calculateTransitionExtension } from '../items/transition/transition-extension-utils';
import { TRANSITION_PRESETS } from '../editor-property-panel/ui/contents/transition/transition-presets';
import {
  TransitionDirection,
  supportsDirection,
  TRANSITION_DIRECTIONS,
  DEFAULT_TRANSITION_DIRECTION,
} from '../editor-property-panel/ui/contents/transition/transition-drag-data';
import {
  AccordionHeader,
  MenuItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SelectFieldTrigger,
  Slider,
  ValueInputField,
} from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';
import { useInspectorSections } from './use-inspector-sections';
import { CollapsibleContent } from './collapsible-content';
import TransitionPreview from '../editor-property-panel/ui/contents/transition/TransitionPreview';
import { resizeCapsuleTransition } from '../state/actions/resize-capsule-transition';

interface TransitionInspectorProps {
  transition: TransitionItem;
  collapseAllTrigger?: number;
  expandAllTrigger?: number;
}

const TransitionInspectorUnmemoized: React.FC<TransitionInspectorProps> = ({
  transition,
  collapseAllTrigger = 0,
  expandAllTrigger = 0,
}) => {
  const t = useTranslations();
  const { fps } = useFps();
  const { setState } = useWriteContext();
  const { items } = useAllItems();
  const { assets } = useAssets();

  const maxDurationInFrames = useMemo(() => {
    const sourceItem = items[transition.sourceItemId];
    const targetItem = items[transition.targetItemId];
    if (!sourceItem || !targetItem) return Infinity;
    return getMaxTransitionDurationInFrames(transition, sourceItem, targetItem);
  }, [items, transition]);

  const maxDurationInSeconds = maxDurationInFrames / fps;
  const minDurationInSeconds = MIN_TRANSITION_DURATION_IN_FRAMES / fps;
  const durationInSeconds = useMemo(
    () => transition.durationInFrames / fps,
    [transition.durationInFrames, fps],
  );
  const [previewProgress, setPreviewProgress] = useState(0);
  const [isPreviewHovered, setIsPreviewHovered] = useState(false);

  useEffect(() => {
    if (!isPreviewHovered) {
      setPreviewProgress(0);
      return;
    }

    let animationFrameId = 0;
    let startAt = 0;
    const cycleMs = Math.max(600, durationInSeconds * 1000);

    const animate = (timestamp: number) => {
      if (!startAt) startAt = timestamp;
      const elapsed = timestamp - startAt;
      setPreviewProgress((elapsed % cycleMs) / cycleMs);
      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [
    durationInSeconds,
    isPreviewHovered,
    transition.direction,
    transition.id,
    transition.transitionType,
  ]);

  const boundaryPoint = useMemo(() => {
    const sourceItem = items[transition.sourceItemId];
    if (!sourceItem) return transition.from + transition.durationInFrames / 2;
    return sourceItem.from + sourceItem.durationInFrames;
  }, [
    items,
    transition.sourceItemId,
    transition.from,
    transition.durationInFrames,
  ]);

  const handleDurationChange = useCallback(
    (newDurationInSeconds: number, commitToUndoStack: boolean) => {
      const clampedDuration = Math.min(
        Math.max(newDurationInSeconds, minDurationInSeconds),
        maxDurationInSeconds,
      );
      const newDurationInFrames = Math.round(clampedDuration * fps);
      const newFrom = Math.round(boundaryPoint - newDurationInFrames / 2);

      const sourceItem = items[transition.sourceItemId];
      const targetItem = items[transition.targetItemId];

      setState({
        update: (state) => {
          const existingTransition =
            state.undoableState.transitions[transition.id];
          if (!existingTransition) return state;

          if (existingTransition.transitionType === 'capsuleCover') {
            return resizeCapsuleTransition({
              state,
              transitionId: transition.id,
              durationInFrames: newDurationInFrames,
            });
          }

          let sourceFrozenFrames = 0;
          let targetFrozenFrames = 0;
          let sourceExtendedFrames = 0;
          let targetExtendedFrames = 0;
          let targetExtendedAssetStartDelta = 0;

          if (sourceItem && targetItem) {
            const extension = calculateTransitionExtension({
              sourceItem,
              targetItem,
              assets,
              fps,
              transitionFrom: newFrom,
              transitionDuration: newDurationInFrames,
            });
            sourceFrozenFrames = extension.sourceFrozenFrames;
            targetFrozenFrames = extension.targetFrozenFrames;
            sourceExtendedFrames = extension.sourceExtendedFrames;
            targetExtendedFrames = extension.targetExtendedFrames;
            targetExtendedAssetStartDelta =
              extension.targetExtendedAssetStartDelta;
          }

          return {
            ...state,
            undoableState: {
              ...state.undoableState,
              transitions: {
                ...state.undoableState.transitions,
                [transition.id]: {
                  ...existingTransition,
                  durationInFrames: newDurationInFrames,
                  from: newFrom,
                  sourceFrozenFrames,
                  targetFrozenFrames,
                  sourceExtendedFrames,
                  targetExtendedFrames,
                  targetExtendedAssetStartDelta,
                },
              },
            },
          };
        },
        commitToUndoStack,
      });
    },
    [
      assets,
      boundaryPoint,
      fps,
      items,
      maxDurationInSeconds,
      minDurationInSeconds,
      setState,
      transition.id,
      transition.sourceItemId,
      transition.targetItemId,
    ],
  );

  const handleDirectionChange = useCallback(
    (newDirection: TransitionDirection) => {
      setState({
        update: (state) => {
          const existingTransition =
            state.undoableState.transitions[transition.id];
          if (!existingTransition) return state;

          return {
            ...state,
            undoableState: {
              ...state.undoableState,
              transitions: {
                ...state.undoableState.transitions,
                [transition.id]: {
                  ...existingTransition,
                  direction: newDirection,
                },
              },
            },
          };
        },
        commitToUndoStack: true,
      });
    },
    [setState, transition.id],
  );

  const isDirectionSupported = useMemo(
    () => supportsDirection(transition.transitionType),
    [transition.transitionType],
  );

  const currentDirection = useMemo(() => {
    const dir = transition.direction ?? DEFAULT_TRANSITION_DIRECTION;
    return TRANSITION_DIRECTIONS.find((d) => d.value === dir);
  }, [transition.direction]);

  const currentPresetName = useMemo(
    () =>
      transition.transitionType === 'capsuleCover'
        ? 'Capsule Cover'
        : (TRANSITION_PRESETS.find(
            (preset) => preset.type === transition.transitionType,
          )?.name ?? TRANSITION_PRESETS[0]?.name),
    [transition.transitionType],
  );

  const { openSections, toggleSection } = useInspectorSections({
    itemId: transition.id,
    defaults: {
      type: true,
      direction: true,
      duration: true,
    },
    collapseAllTrigger,
    expandAllTrigger,
    sectionActive: {
      type: true,
      direction: isDirectionSupported,
      duration: true,
    },
  });

  return (
    <div className="layout-padding-bottom">
      <div className="flex flex-col gap-2xs">
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <AccordionHeader
            size="sm"
            title={t('Editor.inspector.transition.preview')}
            isOpened={openSections.type}
            onClick={() => toggleSection('type')}
          />
          <CollapsibleContent isOpen={openSections.type}>
            <div className="px-md py-4xs">
              <div className="overflow-hidden rounded-sm">
                <div
                  className="aspect-video w-full overflow-hidden rounded-sm"
                  onMouseEnter={() => setIsPreviewHovered(true)}
                  onMouseLeave={() => setIsPreviewHovered(false)}
                >
                  <TransitionPreview
                    type={transition.transitionType}
                    direction={transition.direction}
                    progress={previewProgress}
                    previewDurationInFrames={transition.durationInFrames}
                    primaryColor={transition.primaryColor}
                    secondaryColor={transition.secondaryColor}
                  />
                </div>
                <div className="px-4xs py-2xs text-title-small text-sys-cont-neutral-default">
                  {currentPresetName}
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </div>

        {isDirectionSupported && (
          <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
            <AccordionHeader
              size="sm"
              title={t('Editor.inspector.transition.direction')}
              isOpened={openSections.direction}
              onClick={() => toggleSection('direction')}
            />
            <CollapsibleContent isOpen={openSections.direction}>
              <div className="px-md py-4xs">
                <Popover>
                  <PopoverTrigger asChild>
                    <SelectFieldTrigger value={currentDirection?.label} />
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="w-[var(--radix-popover-trigger-width)] min-w-[120px] rounded-xs border border-sys-style-neutral-default bg-sys-bg-surface-1 px-3xs py-2xs shadow-[0px_0px_16px_0px_rgba(4,4,5,0.08)]"
                  >
                    {TRANSITION_DIRECTIONS.map((dir) => (
                      <MenuItem
                        key={dir.value}
                        size="md"
                        text={dir.label}
                        singleSelectType
                        className="w-full rounded-xs"
                        isSelected={
                          (transition.direction ??
                            DEFAULT_TRANSITION_DIRECTION) === dir.value
                        }
                        onClick={() => handleDirectionChange(dir.value)}
                      />
                    ))}
                  </PopoverContent>
                </Popover>
              </div>
            </CollapsibleContent>
          </div>
        )}

        <div className="px-4xs pb-2xs">
          <AccordionHeader
            size="sm"
            title={t('Editor.inspector.transition.duration')}
            isOpened={openSections.duration}
            onClick={() => toggleSection('duration')}
          />
          <CollapsibleContent isOpen={openSections.duration}>
            <div className="flex flex-col gap-2xs px-md py-4xs">
              <div className="flex w-full items-center gap-3">
                <Slider
                  value={[durationInSeconds]}
                  onValueChange={(values) =>
                    handleDurationChange(values[0] ?? 0, false)
                  }
                  onValueCommit={(values) =>
                    handleDurationChange(values[0] ?? 0, true)
                  }
                  min={minDurationInSeconds}
                  max={maxDurationInSeconds}
                  step={0.1}
                  className="flex-1"
                  isShowTooltip={false}
                  aria-label={`${t('Editor.inspector.transition.duration')}: ${durationInSeconds.toFixed(1)}s`}
                />
                <ValueInputField
                  value={Number(durationInSeconds.toFixed(1))}
                  setValue={({ num, commitToUndoStack }) =>
                    handleDurationChange(num, commitToUndoStack)
                  }
                  min={minDurationInSeconds}
                  max={maxDurationInSeconds}
                  step={0.1}
                  isUnit="s"
                  accessibilityLabel={t('Editor.inspector.transition.duration')}
                  className="w-5xl"
                />
              </div>
              <div className="flex items-center gap-4xs text-caption-small">
                <span className="text-sys-cont-neutral-light">
                  {t('Editor.inspector.transition.frames')}
                </span>
                <span className="text-sys-cont-neutral-default">
                  {transition.durationInFrames} frames
                </span>
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </div>
    </div>
  );
};

export const TransitionInspector = memo(TransitionInspectorUnmemoized);
